import React from 'react';

import imageUrlBuilder from '@sanity/image-url';

import { toKey, types } from '@tmap/mmm-core/entityKey';
import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import useFormattedDate from '@tmap/mmm-style-guide/src/ssr/useFormattedDate';

import dynamic from 'next/dynamic';
import Link from 'next/link';

import client from '../../client';
import BlockContent from '../../components/block-content';
import Metatags from '../../components/metatags';
import PreloadablePicture from '../../components/preloadablePicture';
import FullWrapper from '../../layouts/fullWrapper';
import setCacheHeaders from '../../lib/setCacheHeaders';
import useCurrentUser from '../../hooks/useCurrentUser';
import ColorBlocks from '../../layouts/colorBlocks';
import CardAd from '../../components/cardAd';

const DynamicShareButton = dynamic(() => import('../../components/shareButton'));
const builder = imageUrlBuilder(client);

const LeadImageWrapper = styled(Grid)(({ theme }) => ({
  paddingTop: '50px',
  background: 'linear-gradient(0deg, white 30%, rgba(200,193,187,0.15) 0%)',
  paddingLeft: '10%',
  paddingRight: '10%',
  paddingBottom: theme.spacing(6),
  [theme.breakpoints.down('md')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const leadImageStyle = () => ({
  width: '100%',
  height: 'auto',
  borderRadius: '8px',
});
const Preloader = styled(PreloadablePicture)(leadImageStyle);
const LeadImg = styled('img')(leadImageStyle);

const ArticleBody = styled(Grid)(({ theme }) => ({
  maxWidth: '690px',
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

const ShareBarWrapper = styled(Grid)(({ theme }) => ({
  paddingBottom: theme.spacing(4),
}));

const ContentWrapper = styled(Grid)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

function NewsPage(props) {
  const {
    article,
    contentSettings,
  } = props;
  const canonicalUrl = `${process.env.NEXT_PUBLIC_CANONICAL_DOMAIN}/news/${article.slug.current}`;
  const image = article.heroImage;
  const createdAt = useFormattedDate(article._createdAt, 'MMM d, yyyy');
  const user = useCurrentUser();


  return (
    <>
      <Metatags
        title={article.title}
        description={article.description || ''}
        canonical={canonicalUrl}
        {...(article.heroImage ? { image: builder.image(image).url() } : {})}
      />
      <FullWrapper>
        <LeadImageWrapper container>
          <Grid item xs={12}>
            <Preloader preload>
              <source
                srcSet={
                  builder
                    .image(image)
                    .format('webp')
                    .width(1440)
                    .height(675)
                    .url()
                }
                media='(min-width: 960px)'
              />
              <source
                srcSet={
                  builder
                    .image(image)
                    .format('webp')
                    .width(560)
                    .height(263)
                    .url()
                }
                media='(min-width: 600px) and (max-width: 959px)'
              />
              <source
                srcSet={
                  builder
                    .image(image)
                    .format('webp')
                    .width(375)
                    .height(176)
                    .url()
                }
                media='(max-width: 599px)'
              />
              <LeadImg
                src={
                  builder
                    .image(image)
                    .width(1440)
                    .height(675)
                    .url()
                }
                width={1440}
                height={675}
                alt={(image || {}).alt}
              />
            </Preloader>
          </Grid>
        </LeadImageWrapper>
        <ArticleBody container>
          <Grid item xs={12}>
            <ShareBarWrapper container alignItems='center' spacing={1}>
              {article.pressName && (
                <Grid item>
                  <Typography variant='body2'>{article.pressName}&nbsp;&nbsp;&nbsp;</Typography>
                </Grid>
              )}
              <Grid item xs>
                <Typography variant='body2' color='textSecondary'>{createdAt}</Typography>
              </Grid>
              <Grid item>
                <DynamicShareButton url={canonicalUrl} entityKey={toKey(types.NEWS, article._id)} />
              </Grid>
            </ShareBarWrapper>
          </Grid>
          <Grid item xs={12}>
            <Typography variant='h2' component='h1' color='dark' fontFamily='serif' gutterBottom sx={{ fontWeight: 700 }}>{article.title}</Typography>
            {article.byline && (
              <Typography variant='h6' color='textSecondary' sx={{ paddingBottom: '48px' }}>By {article.byline}</Typography>
            )}
          </Grid>
          <ContentWrapper item xs={12}>
            <BlockContent value={article.body} nested />
          </ContentWrapper>
          {article.sourceUrl && (
            <Grid item xs={12} spacing={6} mb={5}>
              <Link href={article.sourceUrl}><Button color='convert' variant='outlined'>Read Full Article{article.pressName ? ` ${article.pressName}` : ''}</Button></Link>
            </Grid>
          )}
          <Grid item xs={12} sx={{ '& > .MuiBox-root': { padding: 0, mb: 5 } }}>
            {!user._id && (
              <ColorBlocks.Interstitial colorTop='light' colorBottom='light'>
                <CardAd
                  leadCopy={contentSettings.notificationBlockLead}
                  subleadCopy={contentSettings.notificationBlockSubLead}
                  mobile
                />
              </ColorBlocks.Interstitial>
            )}
          </Grid>
        </ArticleBody>
      </FullWrapper>
    </>
  );
}

export const getServerSideProps = async (context) => {
  const { slug = '' } = context.query;

  const result = await client.fetch(`{
      "article":*[_type == "news" && slug.current == $slug][0],
      "contentSettings":*[_id == "contentPageSettings"][0],
    }`, { slug });

  if (!result) {
    return { notFound: true };
  }
  setCacheHeaders(context.res, 300, 3600);
  const analyticsProps = {
    pageName: 'article',
    entityKey: toKey(types.NEWS, result._id),
  };
  return {
    props:
    {
      analyticsProps,
      ...result,
    },
  };
};

export default NewsPage;
